import { Col, Row, Tooltip } from 'antd'
import classNames from 'classnames'
import { MyForm } from '~/components/ui/MyForm'
import { QuestionCircleOutlined } from '@ant-design/icons'
import styles from '../AiForm.module.css'
import useOptions from './useOptions'
import { useAtom } from 'jotai'
import { styleDecorationAtom } from './atom'
import { useCallback, useEffect, useState } from 'react'
import { MyBtn } from '~/components/ui/MyBtn'
import { OptionsTabs } from '../OptionsTabs'
import { MyModal } from '~/components/ui/MyModal'
import {
  AiArtworkOptionTypeEnum,
  getAIMenu_pictureGraph_aiCreateArtworkOptionMenu_adStyleDecorationOptions,
} from '~/graphqls/types'
import { uniqueFunc } from '~/utils'
import { MyIcon } from '~/components/ui/MyIcon'

type StyleDecorationOptionProps = Partial<
  Omit<
    getAIMenu_pictureGraph_aiCreateArtworkOptionMenu_adStyleDecorationOptions,
    '__typename'
  >
>

const StyleDecorationItem: React.FC<{
  isActive: boolean
  isDisabled: boolean
  onChecked: () => void
  data: StyleDecorationOptionProps
}> = ({ isActive, isDisabled, onChecked, data }) => {
  return (
    <div
      className={classNames(
        'rounded-full p-1 text-neutral-800 font-semibold text-sm border-2 text-center cursor-pointer',
        [[isActive ? 'border-neutral-500' : 'border-transparent']],
        {
          'cursor-not-allowed': isDisabled && !isActive,
        }
      )}
      style={{
        background: isActive
          ? 'radial-gradient(74.76% 334.54% at 49.84% 50%, #374176 0%, rgba(55, 65, 118, 0) 100%)'
          : '#23262F',
      }}
      onClick={onChecked}
    >
      {data.name}
    </div>
  )
}

/** 默认显示数量 */
const DEFAULT_SHOW_COUNT = 7
const max = 10

export const StyleDecorationComp = () => {
  const [styleDecoration, setStyleDecoration] = useAtom(styleDecorationAtom)

  const { styleDecorationOptions } = useOptions()

  const [visible, setVisible] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(0)

  const tabs = Array.from(
    new Set(
      styleDecorationOptions
        ?.filter(item => !!item?.category)
        ?.map(item => item?.category!)
    )
  )

  const isDisabled = styleDecoration.length >= max

  const onChecked = useCallback(
    (item: StyleDecorationOptionProps, isActive: boolean) => {
      if (isActive) {
        setStyleDecoration(current => current.filter(it => it.key !== item.key))
      } else {
        if (styleDecoration.length >= max) return

        setStyleDecoration(current => [
          ...current,
          {
            type: item.type as AiArtworkOptionTypeEnum,
            key: item.key!,
          },
        ])
      }
    },
    [styleDecoration, max]
  )

  const defaultOptions = uniqueFunc(
    [
      ...styleDecoration.map(item => {
        return {
          ...styleDecorationOptions.find(o => o?.key === item.key),
        }
      }),
      ...styleDecorationOptions?.slice(0, DEFAULT_SHOW_COUNT),
    ],
    'key'
  )

  /** 风格选择选项变了，过滤掉当前选中的，但是不在风格选择选项中的项目 */
  useEffect(() => {
    setStyleDecoration(
      styleDecoration.filter(
        it => styleDecorationOptions.findIndex(s => s?.key === it.key) > -1
      )
    )
  }, [styleDecorationOptions])

  return (
    <>
      <MyForm.Item
        label={
          <span>
            风格选择{' '}
            <Tooltip
              placement="topLeft"
              arrowPointAtCenter
              title="包含风格修饰、艺术家、元素魔法，支持多选"
            >
              <QuestionCircleOutlined />
            </Tooltip>
          </span>
        }
        className={classNames(styles.borderLessFormItem, '!mb-5')}
      >
        <Row gutter={12}>
          {defaultOptions.map(item => {
            const currentItem = styleDecoration.find(
              (it: any) => (it.key as string) === item.key
            )
            const isActive =
              styleDecoration.findIndex(
                (it: any) => (it.key as string) === item.key
              ) > -1
            return (
              <Col span={6} key={item.key} className="mb-3">
                <StyleDecorationItem
                  isActive={isActive}
                  isDisabled={isDisabled}
                  data={{ ...item, ...currentItem }}
                  onChecked={() => {
                    onChecked(item, isActive)
                  }}
                />
              </Col>
            )
          })}
          <Col span={6} className=" mb-3">
            <div
              className="rounded-full p-1 text-neutral-800 font-semibold text-sm border-2 border-neutral-300 text-center cursor-pointer flex items-center justify-center"
              style={{
                background: 'rgba(35, 38, 47, 0.32)',
              }}
              onClick={() => setVisible(true)}
            >
              <MyIcon icon={'more1'} className="font-normal" />
              <span className="ml-0.5">更多</span>
            </div>
          </Col>
        </Row>
      </MyForm.Item>
      <MyModal
        open={visible}
        title={
          <>
            风格选择 ({styleDecoration.length}/{max})
          </>
        }
        onCancel={() => setVisible(false)}
        maskStyle={{
          backgroundColor: 'rgba(0,0,0,0.8)',
        }}
        dark
        width={720}
        footer={null}
      >
        <OptionsTabs
          options={tabs}
          currentIndex={currentIndex}
          setCurrentIndex={setCurrentIndex}
        />

        <Row
          gutter={12}
          className="mb-3 max-h-[400px] overflow-y-auto scrollbar"
        >
          {styleDecorationOptions
            .filter(
              item => !item?.category || item.category === tabs[currentIndex]
            )
            .map(item => {
              const currentItem = styleDecoration.find(
                (it: any) => (it.key as string) === item?.key
              )
              const isActive =
                styleDecoration.findIndex(
                  (it: any) => (it.key as string) === item?.key
                ) > -1
              return (
                <Col key={item?.key} span={6} className="mb-3">
                  <StyleDecorationItem
                    isDisabled={isDisabled}
                    isActive={isActive}
                    data={{ ...item, ...currentItem }}
                    onChecked={() => {
                      onChecked(item!, isActive)
                    }}
                  />
                </Col>
              )
            })}
        </Row>
        <div className="flex justify-between items-center gap-3">
          <MyBtn
            block
            type="default"
            className="!bg-neutral-200 !border-neutral-200 !text-neutral-800"
            onClick={() => {
              setStyleDecoration([])
              setVisible(false)
            }}
          >
            重置
          </MyBtn>
          <MyBtn block type="primary" onClick={() => setVisible(false)}>
            确定
          </MyBtn>
        </div>
      </MyModal>
    </>
  )
}
